import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowRightIcon,
  CalendarDaysIcon,
  ClockIcon,
  EnvelopeIcon,
  MapPinIcon,
  PhoneIcon
} from '@heroicons/react/24/outline';
import { getPublicOrganizationSlug } from '../utils/publicOrganizationHost';

const formatDate = (value) => {
  if (!value) return 'Date to be announced';
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC'
  }).format(new Date(`${value}T00:00:00Z`));
};

const formatTime = (value) => {
  if (!value) return 'Time to be announced';
  const [hours, minutes] = value.split(':').map(Number);
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit'
  }).format(new Date(2020, 0, 1, hours, minutes));
};

const readableAddress = (address) => {
  if (!address || typeof address !== 'object') return '';
  return [
    address.street || address.address1,
    address.city,
    address.state,
    address.zipCode || address.zip
  ].filter(Boolean).join(', ');
};

const PublicOrganization = () => {
  const slug = useMemo(() => getPublicOrganizationSlug(), []);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!slug) {
      setError('Organization website not found.');
      setLoading(false);
      return;
    }

    axios.get('/api/public/organizations/resolve', { params: { slug } })
      .then(response => {
        setData(response.data);
        const orgName = response.data?.organization?.name;
        if (orgName) document.title = `${orgName} | FieldHouse`;
      })
      .catch(err => {
        setError(err.response?.status === 404
          ? 'This organization website is not published yet.'
          : 'We could not load this organization website.');
      })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center text-white">
        <div className="h-10 w-10 rounded-full border-2 border-white/30 border-t-white animate-spin" />
      </div>
    );
  }

  if (error || !data?.organization) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center px-6 text-center text-white">
        <div>
          <p className="text-sm uppercase tracking-[0.2em] text-slate-400">FieldHouse</p>
          <h1 className="mt-3 text-3xl font-bold">{error || 'Organization website not found.'}</h1>
          <Link to="/login" className="inline-flex mt-8 rounded-full bg-white px-5 py-3 font-semibold text-slate-950">
            Member sign in
          </Link>
        </div>
      </div>
    );
  }

  const { organization: org } = data;
  const registrationEvents = Array.isArray(data.registrationEvents) ? data.registrationEvents : [];
  const site = org.publicSite || {};
  const primary = org.colors?.primary || '#1d4ed8';
  const secondary = org.colors?.secondary || '#0f172a';
  const contactAddress = readableAddress(org.contact?.address);
  const hasContact = org.contact?.email || org.contact?.phone || contactAddress;

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <header className="absolute inset-x-0 top-0 z-20">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 h-24 flex items-center justify-between">
          <a href="#top" className="flex items-center gap-3 text-white">
            {org.logo ? (
              <img src={org.logo} alt={`${org.name} logo`} className="h-14 w-auto max-w-[180px] object-contain" />
            ) : (
              <span className="h-11 w-11 rounded-xl bg-white/15 border border-white/20 flex items-center justify-center font-bold">
                {org.name.charAt(0)}
              </span>
            )}
            <span className="hidden sm:block text-lg font-bold">{org.name}</span>
          </a>
          <nav className="flex items-center gap-3">
            <a href="#registrations" className="hidden sm:inline text-sm font-semibold text-white/90 hover:text-white">
              Tryouts &amp; evaluations
            </a>
            <Link to="/login" className="rounded-full border border-white/40 bg-white/10 px-4 py-2 text-sm font-semibold text-white backdrop-blur hover:bg-white/20">
              Member sign in
            </Link>
          </nav>
        </div>
      </header>

      <main id="top">
        <section
          className="relative min-h-[680px] flex items-end overflow-hidden"
          style={{ background: `linear-gradient(135deg, ${primary}, ${secondary})` }}
        >
          {site.heroImageUrl && (
            <img src={site.heroImageUrl} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/25 to-slate-950/20" />
          <div className="absolute -right-24 top-20 h-96 w-96 rounded-full border-[70px] border-white/5" />
          <div className="relative z-10 mx-auto w-full max-w-7xl px-5 sm:px-8 pb-20 pt-40">
            <p className="text-sm font-bold uppercase tracking-[0.25em] text-white/70">
              {org.sport || 'Youth sports'}
            </p>
            <h1 className="mt-5 max-w-4xl text-5xl font-black tracking-tight text-white sm:text-7xl">
              {site.headline || `Welcome to ${org.name}`}
            </h1>
            <p className="mt-6 max-w-2xl whitespace-pre-line text-lg leading-8 text-white/80 sm:text-xl">
              {site.welcomeText || org.description || 'Development, competition, and a great team experience start here.'}
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <a href="#registrations" className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 font-bold text-slate-950 shadow-xl">
                View open registrations
                <ArrowRightIcon className="h-4 w-4" />
              </a>
              {org.contact?.email && (
                <a href={`mailto:${org.contact.email}`} className="rounded-full border border-white/30 bg-white/10 px-6 py-3 font-bold text-white backdrop-blur">
                  Contact us
                </a>
              )}
            </div>
          </div>
        </section>

        <section id="registrations" className="bg-slate-50 py-20 sm:py-28">
          <div className="mx-auto max-w-7xl px-5 sm:px-8">
            <div className="max-w-2xl">
              <p className="text-sm font-bold uppercase tracking-[0.2em]" style={{ color: primary }}>
                Open registration
              </p>
              <h2 className="mt-3 text-4xl font-black tracking-tight sm:text-5xl">Find your next opportunity.</h2>
              <p className="mt-4 text-lg leading-8 text-slate-600">
                Register as a guest and pay securely online. If your player is placed on a team, we’ll email you an invitation to create your FieldHouse login.
              </p>
            </div>

            {registrationEvents.length > 0 ? (
              <div className="mt-12 grid gap-6 lg:grid-cols-2">
                {registrationEvents.map(event => {
                  const place = event.location || readableAddress(event.address) || 'Location to be announced';
                  return (
                    <article key={event.id} className="group rounded-3xl border border-slate-200 bg-white p-7 shadow-sm transition hover:-translate-y-1 hover:shadow-xl">
                      <div className="flex items-start justify-between gap-5">
                        <div>
                          {event.ageGroup && (
                            <span className="inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-bold uppercase tracking-wide text-slate-600">
                              {event.ageGroup}
                            </span>
                          )}
                          <h3 className="mt-4 text-2xl font-extrabold">{event.title}</h3>
                        </div>
                        <div className="shrink-0 text-right">
                          <p className="text-xs font-semibold uppercase text-slate-400">Registration</p>
                          <p className="mt-1 text-xl font-black">
                            {event.registrationFee > 0 ? `$${event.registrationFee.toFixed(2)}` : 'Free'}
                          </p>
                        </div>
                      </div>
                      {event.description && (
                        <p className="mt-4 line-clamp-3 leading-7 text-slate-600">{event.description}</p>
                      )}
                      <dl className="mt-7 grid gap-3 text-sm text-slate-700 sm:grid-cols-2">
                        <div className="flex items-center gap-2">
                          <CalendarDaysIcon className="h-5 w-5 text-slate-400" />
                          <span>{formatDate(event.date)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <ClockIcon className="h-5 w-5 text-slate-400" />
                          <span>{formatTime(event.time)}</span>
                        </div>
                        <div className="flex items-center gap-2 sm:col-span-2">
                          <MapPinIcon className="h-5 w-5 shrink-0 text-slate-400" />
                          <span>{place}</span>
                        </div>
                      </dl>
                      <Link
                        to={`/public/event?token=${encodeURIComponent(event.registrationToken)}`}
                        className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-2xl px-5 py-3.5 font-bold text-white transition group-hover:brightness-110"
                        style={{ backgroundColor: primary }}
                      >
                        Register player
                        <ArrowRightIcon className="h-4 w-4" />
                      </Link>
                    </article>
                  );
                })}
              </div>
            ) : (
              <div className="mt-12 rounded-3xl border border-dashed border-slate-300 bg-white p-10 text-center">
                <h3 className="text-xl font-bold">No registrations are open right now.</h3>
                <p className="mt-2 text-slate-600">Check back soon for upcoming tryouts and evaluations.</p>
              </div>
            )}
          </div>
        </section>

        {(hasContact || org.sponsors.length > 0) && (
          <section className="py-20 sm:py-24">
            <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-2">
              {hasContact && (
                <div>
                  <p className="text-sm font-bold uppercase tracking-[0.2em]" style={{ color: primary }}>Get in touch</p>
                  <h2 className="mt-3 text-3xl font-black">Questions? We’re here to help.</h2>
                  <div className="mt-7 space-y-4 text-slate-600">
                    {org.contact.email && (
                      <a href={`mailto:${org.contact.email}`} className="flex items-center gap-3 hover:text-slate-950">
                        <EnvelopeIcon className="h-5 w-5" /> {org.contact.email}
                      </a>
                    )}
                    {org.contact.phone && (
                      <a href={`tel:${org.contact.phone}`} className="flex items-center gap-3 hover:text-slate-950">
                        <PhoneIcon className="h-5 w-5" /> {org.contact.phone}
                      </a>
                    )}
                    {contactAddress && (
                      <p className="flex items-start gap-3"><MapPinIcon className="mt-0.5 h-5 w-5 shrink-0" /> {contactAddress}</p>
                    )}
                  </div>
                </div>
              )}
              {org.sponsors.length > 0 && (
                <div>
                  <p className="text-sm font-bold uppercase tracking-[0.2em] text-slate-400">Partners</p>
                  <div className="mt-6 flex flex-wrap items-center gap-7">
                    {org.sponsors.map(sponsor => (
                      <a key={sponsor.id || sponsor.name} href={sponsor.website || '#'} target={sponsor.website ? '_blank' : undefined} rel="noreferrer" className="text-lg font-bold text-slate-500">
                        {sponsor.logo ? <img src={sponsor.logo} alt={sponsor.name} className="h-12 w-auto max-w-[150px] object-contain" /> : sponsor.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </section>
        )}
      </main>

      <footer className="bg-slate-950 py-9 text-white">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 px-5 sm:px-8 sm:flex-row sm:items-center sm:justify-between">
          <p className="font-semibold">{org.name}</p>
          <p className="text-sm text-slate-400">Registration and team management powered by FieldHouse.</p>
        </div>
      </footer>
    </div>
  );
};

export default PublicOrganization;
